import * as React from "react";
import { Search, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const DEFAULT_FILTER_DEBOUNCE_MS = 300;
const GLOBAL_SEARCH_WIDTH_CLASS = "w-[247px] max-w-full";
const NUMERIC_INPUT_CLASS = "appearance-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";
const COLUMN_FILTER_CONTROL_CLASS = "h-7 w-full min-w-0 max-w-full rounded-sm border border-input bg-card px-2 py-1 text-[11px] font-normal text-foreground shadow-none outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20";
const TOP_LEVEL_FILTER_CONTROL_CLASS = COLUMN_FILTER_CONTROL_CLASS;
const EMPTY_DATE_RANGE = Object.freeze({ from: "", to: "" });

function copyDateRange(value = EMPTY_DATE_RANGE) {
  const range = value || EMPTY_DATE_RANGE;
  return {
    from: range.from || "",
    to: range.to || "",
  };
}

function createSearchFilterState(overrides = {}) {
  return {
    ...overrides,
    globalSearch: overrides.globalSearch || "",
    topLevelDraft: { ...(overrides.topLevelDraft || {}) },
    topLevelApplied: { ...(overrides.topLevelApplied || {}) },
    columnFilters: { ...(overrides.columnFilters || {}) },
    dateRangeDraft: copyDateRange(overrides.dateRangeDraft),
    dateRangeApplied: copyDateRange(overrides.dateRangeApplied),
  };
}

function clearAllSearchFilters(state = {}) {
  return createSearchFilterState({
    ...state,
    globalSearch: "",
    topLevelDraft: {},
    topLevelApplied: {},
    columnFilters: {},
    dateRangeDraft: EMPTY_DATE_RANGE,
    dateRangeApplied: EMPTY_DATE_RANGE,
  });
}

function hasConstraint(value) {
  if (Array.isArray(value)) return value.some(hasConstraint);
  if (value && typeof value === "object") return Object.values(value).some(hasConstraint);
  return value !== "" && value != null;
}

function countActiveSearchFilterMechanisms(state = {}) {
  return [
    state.globalSearch,
    state.topLevelApplied,
    state.columnFilters,
    state.dateRangeApplied,
  ].filter(hasConstraint).length;
}

function shouldShowClearAll(state) {
  return countActiveSearchFilterMechanisms(state) > 1;
}

function hasTopLevelFilterConstraints(state = {}) {
  return [
    state.topLevelDraft,
    state.topLevelApplied,
    state.dateRangeDraft,
    state.dateRangeApplied,
  ].some(hasConstraint);
}

function getAppliedSearchFilterConstraints(state = {}) {
  return [
    ["globalSearch", state.globalSearch || ""],
    ["topLevelFilters", { ...(state.topLevelApplied || {}) }],
    ["columnFilters", { ...(state.columnFilters || {}) }],
    ["dateRange", copyDateRange(state.dateRangeApplied)],
  ];
}

function getSearchFilterCombination(state = {}, datasetSelectorContext) {
  return [
    ["datasetSelectorContext", datasetSelectorContext],
    ...getAppliedSearchFilterConstraints(state),
  ];
}

function useSearchFilterState({ initialState, onPageReset, dateRangeMode = "immediate" } = {}) {
  const [state, setState] = React.useState(() => createSearchFilterState(initialState));

  const resetPage = React.useCallback(() => onPageReset?.(), [onPageReset]);
  const applyGlobalSearch = React.useCallback((globalSearch) => {
    setState((current) => ({ ...current, globalSearch }));
    resetPage();
  }, [resetPage]);
  const setTopLevelDraft = React.useCallback((update) => {
    setState((current) => ({
      ...current,
      topLevelDraft: typeof update === "function" ? update(current.topLevelDraft) : { ...update },
    }));
  }, []);
  const clearTopLevelDraft = React.useCallback((name) => {
    setState((current) => ({
      ...current,
      topLevelDraft: { ...current.topLevelDraft, [name]: "" },
    }));
  }, []);
  const applyTopLevelFilters = React.useCallback(() => {
    if (dateRangeMode === "draft" && !isValidDateRange(state.dateRangeDraft)) return;
    setState((current) => ({
      ...current,
      topLevelApplied: { ...current.topLevelDraft },
      ...(dateRangeMode === "draft" ? { dateRangeApplied: copyDateRange(current.dateRangeDraft) } : {}),
    }));
    resetPage();
  }, [dateRangeMode, resetPage, state.dateRangeDraft]);
  const applyColumnFilter = React.useCallback((name, value) => {
    setState((current) => ({
      ...current,
      columnFilters: { ...current.columnFilters, [name]: value },
    }));
    resetPage();
  }, [resetPage]);
  const setDateRangeDraft = React.useCallback((dateRangeDraft) => {
    setState((current) => ({ ...current, dateRangeDraft: copyDateRange(dateRangeDraft) }));
  }, []);
  const applyDateRange = React.useCallback((dateRangeApplied) => {
    setState((current) => ({ ...current, dateRangeApplied: copyDateRange(dateRangeApplied) }));
    resetPage();
  }, [resetPage]);
  const clearTopLevelFilters = React.useCallback(() => {
    setState((current) => ({
      ...current,
      topLevelDraft: {},
      topLevelApplied: {},
      dateRangeDraft: copyDateRange(),
      dateRangeApplied: copyDateRange(),
    }));
    resetPage();
  }, [resetPage]);
  const clearAll = React.useCallback(() => {
    setState((current) => clearAllSearchFilters(current));
    resetPage();
  }, [resetPage]);

  return {
    state,
    setState,
    applyGlobalSearch,
    setTopLevelDraft,
    clearTopLevelDraft,
    applyTopLevelFilters,
    applyColumnFilter,
    setDateRangeDraft,
    applyDateRange,
    clearTopLevelFilters,
    clearAll,
  };
}

function useDebouncedApply(onApply, onPageReset, delay = DEFAULT_FILTER_DEBOUNCE_MS) {
  const timerRef = React.useRef(null);
  const applyRef = React.useRef(onApply);
  const resetRef = React.useRef(onPageReset);
  applyRef.current = onApply;
  resetRef.current = onPageReset;

  const cancel = React.useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  React.useEffect(() => cancel, [cancel]);

  const applyNow = React.useCallback((value) => {
    cancel();
    applyRef.current?.(value);
    resetRef.current?.();
  }, [cancel]);

  const schedule = React.useCallback((value) => {
    cancel();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      applyRef.current?.(value);
      resetRef.current?.();
    }, delay);
  }, [cancel, delay]);

  return { applyNow, cancel, schedule };
}

const ClearControl = React.forwardRef(({ label, className, ...props }, ref) => (
  <Button
    ref={ref}
    type="button"
    variant="ghost"
    size="row-icon"
    className={cn("shrink-0 text-muted-foreground hover:text-foreground", className)}
    aria-label={label}
    {...props}
  >
    <X aria-hidden="true" />
  </Button>
));
ClearControl.displayName = "ClearControl";

function GlobalSearch({
  value,
  onValueChange,
  onApply,
  onPageReset,
  debounceMs = DEFAULT_FILTER_DEBOUNCE_MS,
  placeholder = "Global Search",
  className,
  inputProps,
}) {
  const { applyNow, schedule } = useDebouncedApply(onApply, onPageReset, debounceMs);
  const clear = () => {
    onValueChange?.("");
    applyNow("");
  };

  return (
    <div className={cn("relative flex items-center", GLOBAL_SEARCH_WIDTH_CLASS, className)} data-global-search="true">
      <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <Input
        {...inputProps}
        type="search"
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onValueChange?.(next);
          schedule(next);
        }}
        className={cn("h-9 pl-9 pr-9 text-sm [&::-webkit-search-cancel-button]:appearance-none", inputProps?.className)}
        placeholder={placeholder}
        aria-label={inputProps?.["aria-label"] || "Global Search"}
      />
      {value ? <ClearControl className="absolute right-0.5" label="Clear Global Search" onClick={clear} /> : null}
    </div>
  );
}

const TopLevelFilterArea = React.forwardRef(({
  children,
  onSearch,
  searchDisabled = false,
  className,
  contentClassName,
  actionsClassName,
  actions,
  ...props
}, ref) => (
  <section
    ref={ref}
    className={cn("min-w-0 max-w-full rounded-sm border border-border bg-card p-3", className)}
    data-top-level-filter-area="true"
    {...props}
  >
    <div className={cn("min-w-0 max-w-full", contentClassName)} data-top-level-filter-content="true">{children}</div>
    {actions || onSearch ? (
      <div className={cn("mt-3 min-w-0 max-w-full", actionsClassName)} data-top-level-filter-actions="true">
        {actions}
        {onSearch ? <TopLevelFilterSearch onClick={onSearch} disabled={searchDisabled} /> : null}
      </div>
    ) : null}
  </section>
));
TopLevelFilterArea.displayName = "TopLevelFilterArea";

const TopLevelFilterRow = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("min-w-0 max-w-full", className)} {...props} />
));
TopLevelFilterRow.displayName = "TopLevelFilterRow";

const TopLevelFilterField = React.forwardRef(({ label, htmlFor, className, labelClassName, children, ...props }, ref) => (
  <div ref={ref} className={cn("min-w-0 max-w-full", className)} data-top-level-filter-field="true" {...props}>
    <label htmlFor={htmlFor} className={cn("mb-1 block text-[11px] font-medium text-muted-foreground", labelClassName)}>{label}</label>
    {children}
  </div>
));
TopLevelFilterField.displayName = "TopLevelFilterField";

const TopLevelFilterSearch = React.forwardRef(({ className, children = "Search", ...props }, ref) => (
  <Button ref={ref} type="button" size="sm" className={cn("h-7 rounded-sm px-2 text-[11px]", className)} aria-label="Apply top-level filters" {...props}>
    <Search aria-hidden="true" />
    {children}
  </Button>
));
TopLevelFilterSearch.displayName = "TopLevelFilterSearch";

const TopLevelTextFilter = React.forwardRef(({ className, ...props }, ref) => (
  <input ref={ref} {...props} type="text" className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, className)} data-top-level-filter-control="text" />
));
TopLevelTextFilter.displayName = "TopLevelTextFilter";

const TopLevelSelectFilter = React.forwardRef(({ className, children, options = [], placeholder = "All", ...props }, ref) => (
  <select ref={ref} {...props} className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, "block", className)} data-top-level-filter-control="select">
    {children || <>
      <option value="">{placeholder}</option>
      {options.map((option) => {
        const normalized = typeof option === "object" ? option : { value: option, label: option };
        return <option key={normalized.value} value={normalized.value}>{normalized.label}</option>;
      })}
    </>}
  </select>
));
TopLevelSelectFilter.displayName = "TopLevelSelectFilter";

const TopLevelNumericFilter = React.forwardRef(({ className, allowDecimal = true, ...props }, ref) => (
  <input ref={ref} {...props} type="text" inputMode={allowDecimal ? "decimal" : "numeric"} className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, NUMERIC_INPUT_CLASS, className)} data-top-level-filter-control="numeric" />
));
TopLevelNumericFilter.displayName = "TopLevelNumericFilter";

const TopLevelDateFilter = React.forwardRef(({ className, ...props }, ref) => (
  <input ref={ref} {...props} type="date" className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, className)} data-top-level-filter-control="date" />
));
TopLevelDateFilter.displayName = "TopLevelDateFilter";

const TopLevelFilterClear = React.forwardRef(({ className, children = "Clear All", ...props }, ref) => (
  <Button ref={ref} type="button" variant="ghost" size="sm" className={cn("text-muted-foreground", className)} {...props}>
    <X aria-hidden="true" />
    {children}
  </Button>
));
TopLevelFilterClear.displayName = "TopLevelFilterClear";

function isIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function isValidDateRange(value = EMPTY_DATE_RANGE) {
  const { from = "", to = "" } = value || EMPTY_DATE_RANGE;
  if (!from && !to) return true;
  if ((from && !isIsoDate(from)) || (to && !isIsoDate(to))) return false;
  return !from || !to || from <= to;
}

function formatDateRangeDate(value) {
  if (!isIsoDate(value)) return "";
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", ...(year === new Date().getFullYear() ? {} : { year: "numeric" }) })
    .format(new Date(year, month - 1, day));
}

function dateRangeSummary(value = EMPTY_DATE_RANGE) {
  const range = copyDateRange(value);
  if (!range.from && !range.to) return "Date Range";
  if (range.from && !range.to) return `From ${formatDateRangeDate(range.from)}`;
  if (!range.from && range.to) return `To ${formatDateRangeDate(range.to)}`;
  if (range.from && range.to) return `${formatDateRangeDate(range.from)} – ${formatDateRangeDate(range.to)}`;
}

function matchesDateRange(value, range = EMPTY_DATE_RANGE) {
  if (!isIsoDate(value) || !isValidDateRange(range)) return false;
  const normalized = copyDateRange(range);
  if (!normalized.from && !normalized.to) return true;
  if (normalized.from && value < normalized.from) return false;
  if (normalized.to && value > normalized.to) return false;
  return true;
}

function DateRangeSelector({
  value = EMPTY_DATE_RANGE,
  onChange,
  onApply,
  onPageReset,
  mode = "immediate",
  className,
  controlsClassName,
  id,
  label = "Date Range",
  presentation = "filter",
  "aria-label": ariaLabel = "Date Range",
}) {
  const generatedId = React.useId().replace(/:/g, "");
  const controlId = id || `date-range-${generatedId}`;
  const range = copyDateRange(value);
  const invalid = !isValidDateRange(range);
  const update = (next) => {
    onChange?.(next);
    if (mode === "immediate" && isValidDateRange(next)) {
      onApply?.(next);
      onPageReset?.();
    }
  };
  return (
    <div className={cn("min-w-0 max-w-full", className)} data-date-range-mode={mode}>
      {label ? <label className="mb-1 block text-[11px] font-medium text-muted-foreground" id={`${controlId}-label`}>{label}</label> : null}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, "block overflow-hidden text-ellipsis whitespace-nowrap text-left", presentation === "header-action" && "h-10 min-w-[170px] px-4 text-sm font-semibold", controlsClassName)} aria-label={ariaLabel} aria-labelledby={label ? `${controlId}-label` : undefined} aria-invalid={invalid || undefined} data-date-range-trigger="true" data-date-range-presentation={presentation} title={dateRangeSummary(range)}>
            {dateRangeSummary(range)}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] max-w-[calc(100vw-2rem)] rounded-sm p-3" aria-label={`${ariaLabel} editor`}>
          <div className="min-w-0" data-date-range-controls="true">
            <div className="grid min-w-0 grid-cols-2 gap-2">
              <label className="min-w-0 text-[11px] text-muted-foreground" htmlFor={`${controlId}-from`}>From
                <Input id={`${controlId}-from`} type="date" value={range.from} aria-invalid={invalid || undefined} onChange={(event) => update({ from: event.target.value, to: range.to })} className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, "mt-1")} />
              </label>
              <label className="min-w-0 text-[11px] text-muted-foreground" htmlFor={`${controlId}-to`}>To
                <Input id={`${controlId}-to`} type="date" value={range.to} aria-invalid={invalid || undefined} onChange={(event) => update({ from: range.from, to: event.target.value })} className={cn(TOP_LEVEL_FILTER_CONTROL_CLASS, "mt-1")} />
              </label>
            </div>
          </div>
          {invalid && range.from && range.to ? <p className="mt-2 text-[11px] text-destructive" role="alert">From date must be on or before To date.</p> : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ColumnTextFilter({ value, onValueChange, onApply, onPageReset, debounceMs = DEFAULT_FILTER_DEBOUNCE_MS, className, ...props }) {
  const { schedule } = useDebouncedApply(onApply, onPageReset, debounceMs);
  return (
    <div className={cn("w-full min-w-0 max-w-full overflow-hidden", className)}>
      <input
        {...props}
        type="text"
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onValueChange?.(next);
          schedule(next);
        }}
        className={cn(COLUMN_FILTER_CONTROL_CLASS, props.className)}
        data-column-filter-control="text"
      />
    </div>
  );
}

function ColumnSelectFilter({ value, onValueChange, onApply, onPageReset, options = [], placeholder = "All", className, ...props }) {
  const apply = (next) => {
    onValueChange?.(next);
    onApply?.(next);
    onPageReset?.();
  };
  return (
    <div className={cn("w-full min-w-0 max-w-full overflow-hidden", className)}>
      <select
        {...props}
        value={value}
        onChange={(event) => apply(event.target.value)}
        className={cn(COLUMN_FILTER_CONTROL_CLASS, "block", props.className)}
        data-column-filter-control="select"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const normalized = typeof option === "object" ? option : { value: option, label: option };
          return <option key={normalized.value} value={normalized.value}>{normalized.label}</option>;
        })}
      </select>
    </div>
  );
}

function ColumnDateFilter({ value, onValueChange, onApply, onPageReset, className, ...props }) {
  const apply = (next) => {
    onValueChange?.(next);
    if (!next || isIsoDate(next)) {
      onApply?.(next);
      onPageReset?.();
    }
  };
  return (
    <div className={cn("w-full min-w-0 max-w-full overflow-hidden", className)}>
      <input {...props} type="date" value={value} onChange={(event) => apply(event.target.value)} className={cn(COLUMN_FILTER_CONTROL_CLASS, props.className)} data-column-filter-control="date" />
    </div>
  );
}

function numericDraftPattern({ allowDecimal = true, allowNegative = true } = {}) {
  if (allowDecimal && allowNegative) return /^-?\d*(?:\.\d*)?$/;
  if (allowDecimal) return /^\d*(?:\.\d*)?$/;
  if (allowNegative) return /^-?\d*$/;
  return /^\d*$/;
}

function isCompleteNumericValue(value, options) {
  if (value === "") return true;
  if (!numericDraftPattern(options).test(value)) return false;
  return value !== "-" && value !== "." && value !== "-." && Number.isFinite(Number(value));
}

function ColumnNumericFilter({
  value,
  onValueChange,
  onApply,
  onPageReset,
  debounceMs = DEFAULT_FILTER_DEBOUNCE_MS,
  allowDecimal = true,
  allowNegative = true,
  className,
  ...props
}) {
  const options = { allowDecimal, allowNegative };
  const { cancel, schedule } = useDebouncedApply(onApply, onPageReset, debounceMs);
  const change = (next) => {
    if (!numericDraftPattern(options).test(next)) return;
    onValueChange?.(next);
    cancel();
    if (isCompleteNumericValue(next, options)) schedule(next);
  };
  return (
    <div className={cn("w-full min-w-0 max-w-full overflow-hidden", className)}>
      <input
        {...props}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        value={value}
        onChange={(event) => change(event.target.value)}
        className={cn(COLUMN_FILTER_CONTROL_CLASS, NUMERIC_INPUT_CLASS, props.className)}
        data-column-filter-control="numeric"
      />
    </div>
  );
}

function isValidNumericRange(range = {}, options = { allowDecimal: true, allowNegative: false }) {
  const min = range.min ?? "";
  const max = range.max ?? "";
  if (!isCompleteNumericValue(min, options) || !isCompleteNumericValue(max, options)) return false;
  return min === "" || max === "" || Number(min) <= Number(max);
}

function matchesNumericRange(value, range = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || !isValidNumericRange(range)) return false;
  if (range.min !== "" && range.min != null && numeric < Number(range.min)) return false;
  if (range.max !== "" && range.max != null && numeric > Number(range.max)) return false;
  return true;
}

function numericRangeSummary(range = {}) {
  const min = range.min ?? "";
  const max = range.max ?? "";
  if (min !== "" && max !== "") return `${min} – ${max}`;
  if (min !== "") return `≥ ${min}`;
  if (max !== "") return `≤ ${max}`;
  return "Any";
}

function ColumnPriceRangeFilter({
  value = { min: "", max: "" },
  onValueChange,
  onApply,
  onPageReset,
  debounceMs = DEFAULT_FILTER_DEBOUNCE_MS,
  allowDecimal = true,
  precision,
  className,
  "aria-label": ariaLabel = "Price range",
}) {
  const options = { allowDecimal, allowNegative: false };
  const range = { min: value.min ?? "", max: value.max ?? "" };
  const invalid = !isValidNumericRange(range, options);
  const { cancel, schedule } = useDebouncedApply(onApply, onPageReset, debounceMs);
  const change = (name, nextValue) => {
    if (!numericDraftPattern(options).test(nextValue)) return;
    if (precision != null && nextValue.includes(".") && nextValue.split(".")[1].length > precision) return;
    const next = { ...range, [name]: nextValue };
    onValueChange?.(next);
    cancel();
    if (isValidNumericRange(next, options)) schedule(next);
  };
  return (
    <div className={cn("w-full min-w-0 max-w-full overflow-hidden", className)} data-column-range-filter="true">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(COLUMN_FILTER_CONTROL_CLASS, "block overflow-hidden text-ellipsis whitespace-nowrap text-left")}
            aria-label={ariaLabel}
            aria-invalid={invalid || undefined}
            data-column-filter-control="range"
            title={numericRangeSummary(range)}
          >
            {numericRangeSummary(range)}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 rounded-sm p-3" aria-label={`${ariaLabel} editor`}>
          <div className="grid min-w-0 grid-cols-2 gap-2">
            <label className="min-w-0 text-[11px] text-muted-foreground">
              Min
              <input type="text" inputMode={allowDecimal ? "decimal" : "numeric"} value={range.min} placeholder="Min" aria-label={`${ariaLabel} minimum`} aria-invalid={invalid || undefined} onChange={(event) => change("min", event.target.value)} className={cn(COLUMN_FILTER_CONTROL_CLASS, "mt-1", NUMERIC_INPUT_CLASS)} />
            </label>
            <label className="min-w-0 text-[11px] text-muted-foreground">
              Max
              <input type="text" inputMode={allowDecimal ? "decimal" : "numeric"} value={range.max} placeholder="Max" aria-label={`${ariaLabel} maximum`} aria-invalid={invalid || undefined} onChange={(event) => change("max", event.target.value)} className={cn(COLUMN_FILTER_CONTROL_CLASS, "mt-1", NUMERIC_INPUT_CLASS)} />
            </label>
          </div>
          {invalid ? <p className="mt-2 text-[11px] text-destructive" role="alert">Minimum must not exceed maximum.</p> : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const ColumnFilterRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr ref={ref} className={cn("column-filter-row", className)} data-column-filter-row="true" {...props} />
));
ColumnFilterRow.displayName = "ColumnFilterRow";

const ColumnFilterCell = React.forwardRef(({ className, children, ...props }, ref) => (
  <th ref={ref} className={cn("min-w-0 overflow-hidden align-top", className)} data-column-filter-cell="true" {...props}>{children || null}</th>
));
ColumnFilterCell.displayName = "ColumnFilterCell";

const ClearAllFilters = React.forwardRef(({ className, children = "Clear All", ...props }, ref) => (
  <Button ref={ref} type="button" variant="ghost" size="sm" className={cn("ml-auto h-7 px-2 text-[11px] text-muted-foreground", className)} data-column-filter-clear-all="true" {...props}>
    <X aria-hidden="true" />
    {children}
  </Button>
));
ClearAllFilters.displayName = "ClearAllFilters";

export {
  ClearAllFilters,
  COLUMN_FILTER_CONTROL_CLASS,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnNumericFilter,
  ColumnPriceRangeFilter,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  DEFAULT_FILTER_DEBOUNCE_MS,
  EMPTY_DATE_RANGE,
  GLOBAL_SEARCH_WIDTH_CLASS,
  GlobalSearch,
  NUMERIC_INPUT_CLASS,
  TOP_LEVEL_FILTER_CONTROL_CLASS,
  TopLevelFilterArea,
  TopLevelFilterClear,
  TopLevelFilterField,
  TopLevelFilterRow,
  TopLevelFilterSearch,
  TopLevelDateFilter,
  TopLevelNumericFilter,
  TopLevelSelectFilter,
  TopLevelTextFilter,
  clearAllSearchFilters,
  countActiveSearchFilterMechanisms,
  createSearchFilterState,
  getAppliedSearchFilterConstraints,
  getSearchFilterCombination,
  hasTopLevelFilterConstraints,
  isCompleteNumericValue,
  dateRangeSummary,
  isValidDateRange,
  isValidNumericRange,
  matchesNumericRange,
  matchesDateRange,
  numericRangeSummary,
  shouldShowClearAll,
  useSearchFilterState,
};
